import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  REMINIAI_FUNNEL_QUERY_PARAM,
  REMINIAI_FUNNEL_REDIRECT_SEGMENT,
} from '@/services/reminiaiFunnelService/reminiaiFunnelService';
import { useReminiaiFunnelService } from '@/layout/appWrapper/ServiceProvider';

// next.js types query params as string | string[] | undefined: a repeated
// param (e.g. "?reminiai_funnel=a&reminiai_funnel=b") arrives as an array.
const readFunnelParam = (
  query: Record<string, string | string[] | undefined>
): string | undefined => {
  const value = query[REMINIAI_FUNNEL_QUERY_PARAM];
  return Array.isArray(value) ? value[0] : value;
};

export const useReminiaiFunnelRedirectState = (): {
  isRedirectedFromReminiAi: boolean;
  hasResolvedRedirect: boolean;
} => {
  const {
    isRedirectedFromReminiAi,
    saveIsRedirectedFromReminiAi,
    clearIsRedirectedFromReminiAi,
    hasResolvedLanding,
    markLandingResolved,
  } = useReminiaiFunnelService();
  const router = useRouter();
  // Static export prerenders on the server: default to false, read the
  // real state after mount so no window access happens during render.
  const [isRedirected, setIsRedirected] = useState(false);
  // Consumers that must not act before knowing whether the visitor comes
  // from the marketing redirect (e.g. A/B variant gating) can wait for this.
  const [hasResolved, setHasResolved] = useState(false);

  const funnelParam = readFunnelParam(router.query);

  useEffect(() => {
    if (!router.isReady) return;
    setHasResolved(true);

    // The landing-resolved flag lives in the service singleton so it
    // survives AppWrapper's key={router.route} remounts on route changes:
    // only a full page load counts as a fresh landing.
    const isLandingLoad = !hasResolvedLanding();
    if (isLandingLoad) markLandingResolved();

    if (funnelParam === REMINIAI_FUNNEL_REDIRECT_SEGMENT) {
      saveIsRedirectedFromReminiAi();
      setIsRedirected(true);
      return;
    }

    if (funnelParam !== undefined) {
      // A landing reached with a different funnel value is a different
      // experience: drop the persisted redirection state.
      clearIsRedirectedFromReminiAi();
      setIsRedirected(false);
      return;
    }

    if (isLandingLoad) {
      // A fresh page load without the marker is outside the redirected
      // funnel: drop any flag left over from an earlier landing in this
      // tab. Client-side navigations that simply lose the param (e.g. the
      // upload flow deleting router.query.taskId) keep it instead.
      clearIsRedirectedFromReminiAi();
      setIsRedirected(false);
      return;
    }

    if (isRedirectedFromReminiAi()) {
      setIsRedirected(true);
      return;
    }

    setIsRedirected(false);
  }, [
    router.isReady,
    funnelParam,
    isRedirectedFromReminiAi,
    saveIsRedirectedFromReminiAi,
    clearIsRedirectedFromReminiAi,
    hasResolvedLanding,
    markLandingResolved,
  ]);

  return {
    isRedirectedFromReminiAi: isRedirected,
    hasResolvedRedirect: hasResolved,
  };
};

export const useIsRedirectedFromReminiAi = (): boolean =>
  useReminiaiFunnelRedirectState().isRedirectedFromReminiAi;
